import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Paginated } from '../../models/paginated.model';
import {
    CreateOrUpdateOutOfOfficeDelegationPayload,
    OutOfOfficeDelegation,
    OutOfOfficeDelegationsQuery
} from '../models/out-of-office.model';

export type { OutOfOfficeDelegation, OutOfOfficeDelegationsQuery, CreateOrUpdateOutOfOfficeDelegationPayload };

/**
 * API service for managing the current user's out-of-office delegations.
 *
 * This service intentionally avoids state-management coupling (e.g. NGXS)
 * so components can call it directly and own their local UI state.
 */
@Injectable({
    providedIn: 'root'
})
export class OutOfOfficeService {
    private readonly httpClient = inject(HttpClient);
    private readonly myDelegationsBaseUrl = `${environment.apiUrl}/me/out-of-office/delegations`;

    /**
     * Loads delegations for the current user within the provided UTC date range.
     */
    public getMyDelegations(query: OutOfOfficeDelegationsQuery): Observable<Paginated<OutOfOfficeDelegation>> {
        const params = new HttpParams().append('startDate', query.startDate).append('endDate', query.endDate);
        return this.httpClient.get<Paginated<OutOfOfficeDelegation>>(this.myDelegationsBaseUrl, { params });
    }

    /**
     * Creates a new delegation for the current user.
     */
    public createMyDelegation(payload: CreateOrUpdateOutOfOfficeDelegationPayload): Observable<OutOfOfficeDelegation> {
        return this.httpClient.post<OutOfOfficeDelegation>(this.myDelegationsBaseUrl, payload);
    }

    /**
     * Updates an existing delegation for the current user.
     */
    public updateMyDelegation(
        delegationId: string,
        payload: CreateOrUpdateOutOfOfficeDelegationPayload
    ): Observable<OutOfOfficeDelegation> {
        return this.httpClient.patch<OutOfOfficeDelegation>(`${this.myDelegationsBaseUrl}/${delegationId}`, payload);
    }

    /**
     * Deletes an existing delegation for the current user.
     */
    public deleteMyDelegation(delegationId: string): Observable<void> {
        return this.httpClient.delete<void>(`${environment.apiUrl}/out-of-office/delegations/${delegationId}`);
    }
}
